/**
 * BatchListTable Component
 *
 * Table displaying batches with animations, keyboard navigation,
 * and proper status colors using CORRECT database enum values.
 *
 * Status badges use: OPEN | PICKING | PICKED | CATCHING | PACKING | COMPLETED | CANCELLED
 * NOT: NEW | IN_PROGRESS | COMPLETE (incorrect)
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Check, X, AlertTriangle, Package2, ChevronRight } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import type { BatchRecord } from '../types';
import { ANIMATIONS, getAnimationProps } from '@/lib/animations';

interface BatchListTableProps {
  batches: BatchRecord[];
  onBatchClick?: (batchId: string) => void;
  isLoading?: boolean;
}

// Status badge colors - using CORRECT database enum values
// Colors chosen for maximum distinction and colorblind accessibility:
// - Progressive stages use distinct hues (warm→cool spectrum)
// - CANCELLED uses neutral gray (not red) to avoid red/green confusion with COMPLETED
const statusColors: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PICKING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  PICKED: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  CATCHING: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  PACKING: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  CANCELLED: 'bg-slate-100 text-slate-800 dark:bg-slate-800/30 dark:text-slate-400',
};

export function BatchListTable({
  batches,
  onBatchClick,
  isLoading,
}: BatchListTableProps) {
  const { t } = useTranslation('production');
  // Respect user's reduced motion preference
  const shouldReduceMotion = useReducedMotion();
  const rowAnimProps = getAnimationProps(ANIMATIONS.tableRow, shouldReduceMotion);

  // Compute garment and order counts from batch record
  const getGarmentCount = (batch: BatchRecord) => {
    return batch.total_pod_items + batch.total_pap_items + batch.total_redo_items;
  };

  // Empty state
  if (!isLoading && batches.length === 0) {
    return (
      <div className="text-center py-12">
        <Package2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
        <p className="text-muted-foreground">{t('batches.noBatchesYet')}</p>
        <Link
          to="/production/orders"
          className="text-primary hover:underline mt-1 inline-block"
        >
          {t('batches.createFromOrdersLink')}
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('batches.columnBatch')}</TableHead>
            <TableHead className="text-right">{t('batches.columnOrders')}</TableHead>
            <TableHead className="text-right">{t('batches.columnItems')}</TableHead>
            <TableHead>{t('batches.columnPicklist')}</TableHead>
            <TableHead>{t('batches.columnLabels')}</TableHead>
            <TableHead>{t('batches.columnCreated')}</TableHead>
            <TableHead className="w-10"><span className="sr-only">Actions</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <AnimatePresence mode="popLayout">
            {batches.map((batch, index) => (
              <motion.tr
                key={batch.id}
                {...rowAnimProps}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={shouldReduceMotion ? { duration: 0 } : { delay: index * 0.05, duration: 0.2 }}
                className="cursor-pointer hover:bg-muted/50 transition-colors group"
                onClick={() => onBatchClick?.(batch.id)}
                tabIndex={0}
                aria-label={`View batch ${batch.batch_number}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onBatchClick?.(batch.id);
                  }
                }}
              >
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/production/batches/${batch.id}`}
                      className="hover:underline"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      BATCH-{String(batch.batch_number).padStart(3, '0')}
                    </Link>
                    <Badge
                      variant="secondary"
                      className={statusColors[batch.status] || statusColors.OPEN}
                    >
                      {batch.status}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {batch.total_orders}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {getGarmentCount(batch)}
                </TableCell>
                <TableCell>
                  {batch.picklist_printed ? (
                    <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <Check className="h-4 w-4" />
                      <span className="text-sm">{t('batches.printed')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <X className="h-4 w-4" />
                      <span className="text-sm">{t('batches.notPrinted')}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {batch.labels_printed ? (
                    <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <Check className="h-4 w-4" />
                      <span className="text-sm">{t('batches.printed')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-orange-600 dark:text-orange-500">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">{t('batches.notPrinted')}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  <time
                    dateTime={batch.created_at}
                    title={format(new Date(batch.created_at), 'PPpp')}
                  >
                    {formatDistanceToNow(new Date(batch.created_at), {
                      addSuffix: true,
                    })}
                  </time>
                </TableCell>
                <TableCell className="text-right">
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </TableCell>
              </motion.tr>
            ))}
          </AnimatePresence>
        </TableBody>
      </Table>
    </div>
  );
}
